import { data, Form, Link, useSearchParams } from 'react-router'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { userHasPermission, useOptionalUser } from '#app/utils/user.ts'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'Genealogy Index | EBHS Records' },
]

export async function loader({ request }: Route.LoaderArgs) {
	const { searchGenealogyRecords } = await import(
		'#app/models/genealogy-record.server.ts'
	)
	const url = new URL(request.url)
	const term = url.searchParams.get('term')
	const page = parseInt(url.searchParams.get('page') ?? '0', 10)

	// Check if user has genealogy-admin role to show deleted records
	const userId = await getUserId(request)
	let isGenealogyAdmin = false
	if (userId) {
		const user = await prisma.user.findFirst({
			where: {
				id: userId,
				roles: { some: { name: 'genealogy-admin' } },
			},
			select: { id: true },
		})
		isGenealogyAdmin = !!user
	}

	if (!term) {
		return data({
			results: [] as Awaited<
				ReturnType<typeof searchGenealogyRecords>
			>['results'],
			totalCount: 0,
			page: 0,
			pageSize: 50,
			totalPages: 0,
			isGenealogyAdmin,
		})
	}

	const searchResults = await searchGenealogyRecords({
		term,
		page: isNaN(page) ? 0 : page,
		includeDeleted: isGenealogyAdmin,
	})

	return data({ ...searchResults, isGenealogyAdmin })
}

export default function GenealogySearchPage({
	loaderData,
}: Route.ComponentProps) {
	const { results, totalCount, page, totalPages, isGenealogyAdmin } =
		loaderData
	const [searchParams] = useSearchParams()
	const user = useOptionalUser()
	const canCreate = userHasPermission(user, 'create:genealogy:any')

	const term = searchParams.get('term') ?? ''

	function pageLink(pageNum: number) {
		const params = new URLSearchParams(searchParams)
		params.set('page', String(pageNum))
		return `?${params.toString()}`
	}

	return (
		<div className="px-4 pb-8 pt-4">
			<div className="mx-auto max-w-6xl sm:px-6 lg:px-8">
				<header className="py-5">
					<h1 className="text-3xl font-bold leading-tight text-gray-900">
						Genealogy Index
					</h1>
					<p className="mt-2 text-lg text-gray-600">
						Search our index of over 92,000 genealogy records.
					</p>
				</header>

				<Form method="get" className="rounded-lg bg-white p-6 shadow">
					<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
						<div>
							<label
								htmlFor="term"
								className="block text-sm font-medium text-gray-700"
							>
								Name
							</label>
							<input
								type="text"
								id="term"
								name="term"
								defaultValue={term}
								placeholder="Search by name"
								className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
							/>
						</div>
					</div>
					<div className="mt-4 flex gap-3">
						<button
							type="submit"
							className="inline-flex items-center rounded-md bg-ebhs-blue px-4 py-2 text-white shadow-sm hover:bg-ebhs-blue-dark focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
						>
							Search
						</button>
						{canCreate ? (
							<Link
								to="/records/genealogy/admin/new"
								className="inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
							>
								Add Record
							</Link>
						) : null}
					</div>
				</Form>

				{totalCount > 0 ? (
					<div className="mt-6">
						<p className="mb-3 text-sm text-gray-600">
							{totalCount} result{totalCount !== 1 ? 's' : ''} found
							{totalPages > 1
								? ` — page ${page + 1} of ${totalPages}`
								: ''}
						</p>
						<div className="overflow-x-auto rounded-lg bg-white shadow">
							<table className="min-w-full divide-y divide-gray-300">
								<thead>
									<tr>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Surname
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											First Name
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Middle
										</th>
										<th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
											Location
										</th>
									</tr>
								</thead>
								<tbody className="divide-y divide-gray-200">
									{results.map((record) => {
										const isDeleted = !!record.deletedAt
										return (
											<tr
												key={record.id}
												className={isDeleted ? 'bg-red-50 opacity-60' : ''}
											>
												<td className="whitespace-nowrap px-3 py-4 text-sm text-gray-900">
													<Link
														to={`/records/genealogy/${record.id}`}
														className={
															isDeleted
																? 'text-red-400 line-through underline hover:text-red-600'
																: 'text-ebhs-blue underline hover:text-ebhs-blue-dark'
														}
													>
														{record.surname}
													</Link>
													{isDeleted && isGenealogyAdmin ? (
														<span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
															Deleted
														</span>
													) : null}
												</td>
												<td
													className={`whitespace-nowrap px-3 py-4 text-sm ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}
												>
													{record.givenFirst}
												</td>
												<td
													className={`whitespace-nowrap px-3 py-4 text-sm ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}
												>
													{record.givenMiddle}
												</td>
												<td
													className={`whitespace-nowrap px-3 py-4 text-sm ${isDeleted ? 'text-gray-400 line-through' : 'text-gray-500'}`}
												>
													{record.locationId}
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>

						{totalPages > 1 ? (
							<div className="mt-4 flex items-center justify-between">
								{page > 0 ? (
									<Link
										to={pageLink(page - 1)}
										className="rounded-md bg-ebhs-blue px-4 py-2 text-white hover:bg-ebhs-blue-dark"
									>
										Previous
									</Link>
								) : (
									<span />
								)}
								{page < totalPages - 1 ? (
									<Link
										to={pageLink(page + 1)}
										className="rounded-md bg-ebhs-blue px-4 py-2 text-white hover:bg-ebhs-blue-dark"
									>
										Next
									</Link>
								) : (
									<span />
								)}
							</div>
						) : null}
					</div>
				) : searchParams.has('term') ? (
					<p className="mt-6 text-gray-600">
						No results found. Try adjusting your search term.
					</p>
				) : null}
			</div>
		</div>
	)
}
